import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ApplicationState } from "../../../../store";
import * as httpClient from "../../../../util/httpClient";
import { Notification } from "../../../common/Notification";
import * as MyAccountActions from "../../../../store/myAccount/actions";
import { useForm, Controller } from "react-hook-form";
import { PATTERNS } from "../../../../util/patterns";
import { maskEIN, formatEINEditView } from "../../../../util/common";
import { CommonPhoneInputFields } from "../../../../util/CommonMasking";
import i18nService from "../../../../util/Internationalization/i18n/i18n";
import { myAccountServiceStatePII } from "../../../../store/common";
import * as services from "../../../../store/service";
import PhoneInput, { formatPhoneNumber, isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import { isValidPhoneNo } from "../../../../util/phoneNumberValidationUtil";
import { numberOfEmployees } from "../../../../util/CommonLists";
import { useAreServicesActive } from "../../../../util/CommonServices";
import { ezsServiceIds } from "../../../../config/UserServicesList";
import { useTranslation } from "react-i18next";

const BusinessAccountAndContactInformation = (props: any) => {
    const { t: tLang } = useTranslation();
    const dispatch = useDispatch();
    const getAccountInformation = (forceReload?: boolean) => dispatch(MyAccountActions.actionCreators.getAccountInformation(forceReload));
    const accountInfo = useSelector((state: ApplicationState) => (state.myAccountServiceState ? state.myAccountServiceState._unifiedAccountInfo : null));
    const personalAccountInformation = useSelector((state: ApplicationState) =>
        state.myAccountServiceState
            ? state.myAccountServiceState._unifiedAccountInfo
                ? state.myAccountServiceState._unifiedAccountInfo._personalAccountInformation
                : null
            : null
    );
    const businessInformation = useSelector((state: ApplicationState) =>
        state.myAccountServiceState
            ? state.myAccountServiceState._unifiedAccountInfo
                ? state.myAccountServiceState._unifiedAccountInfo._businessInformation
                : null
            : null
    );
    const contactPreference = useSelector((state: ApplicationState) =>
        state.myAccountServiceState
            ? state.myAccountServiceState._unifiedAccountInfo
                ? state.myAccountServiceState._unifiedAccountInfo._contactPreference
                : null
            : null
    );
    const businessProfileStatus = useSelector(
        (state: ApplicationState) =>
            state.loggedInUser &&
            state.loggedInUser.userServiceDetails?.businessProfileStatus?.filter((obj: any) => {
                return obj.provider;
            })
    );
    const creditProvider = businessProfileStatus != null && businessProfileStatus.length > 0 ? businessProfileStatus[0].provider : "";
    const [showEdit, setShowEdit] = useState(false);
    const [isLoading, setisLoading] = useState(false);
    const [errorMsg, seterrorMsg] = useState("");
    const [hasEmailError, setEmailHasError] = useState(false);
    const [zipCode, setZipCode] = useState(i18nService.get("ZipCode"));
    const isDarkWebMonitoringActive = useAreServicesActive([ezsServiceIds.businessDarkWebMonitoring]);
    const [piiState, setPiiState] = useState({} as myAccountServiceStatePII);

    const countryChangeDetected = () => {
        i18nService.setRegionBasedonCountry(watch("country"));
        setZipCode(i18nService.get("ZipCode"));
        setValue("city", "");
        setValue("zipCode", "");
        if (watch("country") !== "US" && watch("country") !== "CA") {
            setValue("state", "");
        } else {
            setValue("state", "---");
        }
    };

    /**Validation with react-hook-form ***/
    const { register, errors, formState, handleSubmit, watch, setValue, control } = useForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const { isSubmitting } = formState;

    const showEditForm = () => {
        setShowEdit(true);
    };

    const hideEditForm = () => {
        setValue("country", accountInfo && !!accountInfo.country?.replaceAll(/\s/g, "") ? accountInfo.country : "US");
        countryChangeDetected();
        setShowEdit(false);
    };

    const save = (accountInformation: any) => {
        if (accountInformation && contactPreference && accountInfo && personalAccountInformation) {
            const requestBody = {
                FirstName: personalAccountInformation.firstName,
                LastName: personalAccountInformation.lastName,
                MiddleName: personalAccountInformation.middleInitial,

                Email: accountInformation.primaryEmail,
                Phone: accountInformation.primaryPhone
                    ? getCountryInfo()
                        ? accountInformation.primaryPhone.replace(/[^0-9+]/g, "")
                        : accountInformation.primaryPhone
                    : accountInformation.primaryPhone,
                BusinessName: accountInformation.businessName,
                EIN: accountInformation.ein ? accountInformation.ein.replace("-", "") : "",
                FAX: accountInformation.fax,
                numberOfEmployees: accountInformation.numberOfEmployees,

                PersonalSMS: contactPreference.smsNumber
                    ? getCountryInfo()
                        ? contactPreference.smsNumber.replace(/[^0-9+]/g, "")
                        : contactPreference.smsNumber
                    : contactPreference.smsNumber,
                IsEmailIdentityNotificationOptOut: contactPreference.idCrimeAndBreachNews,
                IsEmailNotificationOptOut: contactPreference.alertNotification,
                IsPushNotificationOptOut: contactPreference.appPushNotification,
                IsSmsNotificationOptOut: contactPreference.textSMSNotification,
                Address1: accountInfo.address1,
                Address2: accountInfo.address2,
                City: accountInfo.city,
                State: accountInfo.state,
                Country: accountInfo.country,
                ZipCode: accountInfo.zipCode,
                UserName: accountInformation.userName,
            };
            setisLoading(true);
            setEmailHasError(false);

            httpClient
                .sendRequest("POST", "api/accountInfo/saveContactInformation", requestBody)
                .then((data) => {
                    setisLoading(false);
                    if (data.isSuccess) {
                        Notification({
                            type: "success",
                            title: tLang("manageAccount.myAccount.businessAccountAndContactInformation.notification.successTitle"),
                            message: tLang("manageAccount.myAccount.businessAccountAndContactInformation.notification.successMsg"),
                        });
                        getAccountInformation(true);
                        setShowEdit(false);
                    } else if (data.hasError && data.errors.length > 0) {
                        const index = data.errors.findIndex((x: any) => x === "Email id already exists");
                        if (index > -1) {
                            seterrorMsg(data.errors[index]);
                            setEmailHasError(true);
                        }
                    }
                })
                .catch(function (error) {
                    setisLoading(false);
                    Notification({
                        type: "error",
                        title: tLang("manageAccount.myAccount.businessAccountAndContactInformation.notification.errorTitle"),
                        message: tLang("manageAccount.myAccount.businessAccountAndContactInformation.notification.errorMsg"),
                        duration: 0,
                    });
                });
        }
    };

    const getCountryInfo = () => {
        let currentCountry = i18nService.get("Country");
        if (currentCountry.includes("US") || currentCountry.includes("CA")) {
            return false;
        } else {
            return true;
        }
    };

    let EINFieldPressedKey: number;
    const updatEINFieldPressedKey = (e: any) => {
        EINFieldPressedKey = e.keyCode;
    };

    function onEINChangeHandler(event: any) {
        if (event.target.value.length == 2 && EINFieldPressedKey !== 8 && EINFieldPressedKey !== 46) {
            event.target.value = event.target.value + "-";
        }
    }

    const maskPhoneNumber = (phone?: string) => {
        if (!phone) return "";
        if (getCountryInfo()) return phone;
        if (phone.length < 10 || phone.length > 10) return phone;
        return `(${phone.substr(0, 3)}) ${phone.substr(3, 3)}-${phone.substr(6)}`;
    };

    let maxL = getCountryInfo() ? 15 : 14;
    let minL = getCountryInfo() ? 7 : 14;

    let validationFax = getCountryInfo()
        ? register({
              required: false,
              maxLength: maxL,
              minLength: minL,
              pattern: PATTERNS.PHONENUMBER.pattern,
          })
        : register({
              required: false,
              maxLength: maxL,
              minLength: minL,
              pattern: PATTERNS.PHONE.pattern,
          });

    const ensureDataFetched = () => {
        setisLoading(true);

        services.GetAccountInformation().then((accountInfoResponse) => {
            setisLoading(false);
            setPiiState({ _businessInformation: { ein: accountInfoResponse._unifiedAccountInfo._businessInformation.ein } });
        });
        
    };

    useEffect(ensureDataFetched, []);

    useEffect(() => {
        const elements = document.getElementsByClassName("PhoneInputCountry");

        if (elements && elements.length > 0) {
            try {
                for (const el of elements) {
                    const childs = el.children;
                    if (childs[0].nodeName === "SELECT") {
                        const selectEl = childs[0] as HTMLSelectElement;
                        selectEl.disabled = true;
                    }
                }
            } catch {}
        }
        const selectElement = document.getElementsByClassName("PhoneInputCountrySelect");
        if (selectElement && selectElement.length > 0) {
            for (const el of selectElement) {
                if (el.tagName.toLowerCase() === "select") {
                    el.setAttribute("aria-required", "true");
                }
            }
        }
    }, [showEdit]);

    return (
        <React.Fragment>
            <div className="box offset-padding">
                <div className="box-header">
                    <div className="box-title-container">
                        <div className="box-title-icon" aria-hidden="true">
                            <em className="las la-user"></em>
                        </div>
                        <div className="box-title-text has-actions">
                            <h2 className="box-title">{tLang("manageAccount.myAccount.businessAccountAndContactInformation.title")}</h2>
                        </div>
                    </div>

                    <div className="box-description">
                        <p>{tLang("manageAccount.myAccount.businessAccountAndContactInformation.description")}</p>
                    </div>

                    {!showEdit && (
                        <div className="box-actions">
                            <button type="button" className="btn-default btn-grey-dot" id="business-account-contact-info" onClick={() => showEditForm()}>
                                <span className="btn-text">
                                    {tLang("manageAccount.myAccount.membershipInformation.editButtonText")}{" "}
                                    <span className="sr-only">{tLang("manageAccount.myAccount.businessAccountAndContactInformation.title")}</span>
                                </span>
                                <span className="btn-icon">
                                    <em className="las la-pen"></em>
                                </span>
                            </button>
                        </div>
                    )}
                </div>

                <div className={isLoading ? "box-content loading" : "box-content"}>
                    {!showEdit && accountInfo && personalAccountInformation && businessInformation && (
                        <div role="grid" className="data-grid " id="business-account-info-grid" aria-hidden="false">
                            <div role="row" className="data-row row">
                                <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="label-business-name-display">
                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.businessName")}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter" aria-labelledby="label-business-name-display">
                                    <div className="data-value">
                                        {businessInformation.businessName}
                                    </div>
                                </div>
                            </div>
                            {isDarkWebMonitoringActive && (
                                <div role="row" className="data-row row">
                                    <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                        <div className="data-label" id="label-number-of-employees">
                                            {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.numberOfEmployees")}
                                        </div>
                                    </div>
                                    <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter" aria-labelledby="label-number-of-employees">
                                        <div className="data-value">
                                            {!!businessInformation.numberOfEmployees ? businessInformation.numberOfEmployees : "1-99"}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {accountInfo && accountInfo.isSSOUser === false && (
                                <div role="row" className="data-row row">
                                    <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                        <div className="data-label" id="label-email-display">
                                            {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.primaryEmail")}
                                        </div>
                                    </div>
                                    <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter" aria-labelledby="label-email-display">
                                        <div className="data-value">
                                            {personalAccountInformation.primaryEmail}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div role="row" className="data-row row">
                                <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="label-phone-display">
                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.primaryPhone")}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter" aria-labelledby="label-phone-display">
                                    <div className="data-value">
                                        {personalAccountInformation.primaryPhone && isValidPhoneNumber(personalAccountInformation.primaryPhone)
                                            ? formatPhoneNumber(personalAccountInformation.primaryPhone)
                                            : maskPhoneNumber(personalAccountInformation.primaryPhone)}
                                    </div>
                                </div>
                            </div>

                            <div role="row" className="data-row row">
                                <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="label-fax-display">
                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.fax")}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter" aria-labelledby="label-fax-display">
                                    <div className="data-value">
                                        {maskPhoneNumber(businessInformation.fax)}
                                    </div>
                                </div>
                            </div>
                            {creditProvider != null && creditProvider === "CREDITSAFE" && (
                                <div role="row" className="data-row row">
                                    <div role="gridcell" className="col-xs-12 col-sm-4 col-no-gutter">
                                        <div className="data-label" id="label-ein-display">
                                            {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.ein")}
                                        </div>
                                    </div>
                                    <div role="gridcell" className="col-xs-12 col-sm-8 col-no-gutter" aria-labelledby="label-ein-display">
                                        <div className="data-value">
                                            {maskEIN(piiState?._businessInformation?.ein)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {showEdit && personalAccountInformation && accountInfo && businessInformation && (
                        <div id="business-account-info-form">
                            <form id="business-account-info-form-container" onSubmit={handleSubmit(save)}>
                                <fieldset className="form-row inline">
                                    <div className="row">
                                        <div className="col-xs-12 col-sm-4 col-no-gutter">
                                            <legend className="legend-label">
                                                <label htmlFor="label-business-name">
                                                    {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.businessName")}{" "}
                                                    <span className="required" title="Mandatory">
                                                        *
                                                    </span>
                                                </label>
                                            </legend>
                                        </div>

                                        <div className="col-xs-12 col-sm-4 col-no-gutter">
                                            <div className="row">
                                                <div className="col-xs-12 col-no-gutter">
                                                    <div className={errors["businessName"] ? "form-row row status-error" : "form-row row"}>
                                                        <div className="form-row row">
                                                            <div className="form-label sr-only col-xs-12 col-no-gutter">
                                                                <label htmlFor="label-business-name-1">
                                                                    Business Name {" "}
                                                                    <span className="required" title="Mandatory">
                                                                        *
                                                                    </span>
                                                                </label>
                                                            </div>
                                                            <div role="alert" className="form-input col-xs-12 col-no-gutter">
                                                                <input
                                                                    type="text"
                                                                    className="inputbox"
                                                                    id="label-business-name"
                                                                    name="businessName"
                                                                    placeholder={tLang(
                                                                        "manageAccount.myAccount.businessAccountAndContactInformation.placeholder.businessName"
                                                                    )}
                                                                    defaultValue={businessInformation.businessName}
                                                                    ref={register({ pattern: /^[a-zA-Z0-9-',.&()\s]+$/, required: true })}
                                                                    maxLength={60}
                                                                    aria-required="true"
                                                                />
                                                                {errors.businessName && errors.businessName.type === "pattern" && (
                                                                    <span className="form-error">
                                                                        {tLang(
                                                                            "manageAccount.myAccount.businessAccountAndContactInformation.validation.businessName.pattern"
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {errors.businessName && errors.businessName.type === "required" && (
                                                                    <span className="form-error">
                                                                        {tLang(
                                                                            "manageAccount.myAccount.businessAccountAndContactInformation.validation.businessName.required"
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>

                                {isDarkWebMonitoringActive && (
                                    <div className="form-row inline row">
                                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                            <label htmlFor="plan-employees">
                                                {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.numberOfEmployees")}
                                            </label>
                                        </div>
                                        <div className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                            <select
                                                className="inputbox"
                                                id="plan-employees"
                                                name="numberOfEmployees"
                                                defaultValue={!!businessInformation.numberOfEmployees ? businessInformation.numberOfEmployees : "1-99"}
                                                ref={register}
                                            >
                                                {numberOfEmployees.map((range: any, index: number) => (
                                                    <option key={`range-${index}`} value={range}>
                                                        {range}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {accountInfo && accountInfo.isSSOUser === false && (
                                    <div className={errors["primaryEmail"] ? "form-row row status-error" : "form-row row"}>
                                        <div className="form-row inline row">
                                            <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                                <label htmlFor="label-email">
                                                    {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.primaryEmail")}{" "}
                                                    <span className="required" title="Mandatory">
                                                        *
                                                    </span>
                                                </label>
                                            </div>
                                            <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                                <input
                                                    type="text"
                                                    className="inputbox"
                                                    id="label-email"
                                                    placeholder={tLang("manageAccount.myAccount.businessAccountAndContactInformation.placeholder.primaryEmail")}
                                                    name="primaryEmail"
                                                    defaultValue={personalAccountInformation.primaryEmail}
                                                    maxLength={PATTERNS.EMAIL.maxLength}
                                                    ref={register({
                                                        required: true,
                                                        pattern: PATTERNS.EMAIL.pattern,
                                                    })}
                                                    aria-required="true"
                                                />
                                                {errors.primaryEmail && errors.primaryEmail.type === "required" && (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryEmail.required")}
                                                    </span>
                                                )}
                                                {errors.primaryEmail && errors.primaryEmail.type === "pattern" && (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryEmail.pattern")}
                                                    </span>
                                                )}
                                                {hasEmailError && (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryEmail.exist")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <fieldset className="form-row no-margin inline">
                                    <div className="row">
                                        <div className="col-xs-12 col-sm-4 col-no-gutter">
                                            <legend className="legend-label">
                                                <label htmlFor="label-phone-number">
                                                    {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.primaryPhone")}{" "}
                                                    <span className="required" title="Mandatory">
                                                        *
                                                    </span>
                                                </label>
                                            </legend>
                                        </div>

                                        <div className="col-xs-12 col-sm-4 col-no-gutter">
                                            <div className="row ">
                                                <div className="col-xs-12 col-no-gutter">
                                                    <div
                                                        className={`form-row row ${errors["primaryPhone"] && "status-error"} ${
                                                            errors["primaryPhone"] && watch("primaryPhone") && !parsePhoneNumber(watch("primaryPhone"))
                                                                ? "country-error"
                                                                : ""
                                                        }`}
                                                    >
                                                        <div className="form-row row">
                                                            <div className="form-label sr-only col-xs-12  col-no-gutter">
                                                                <label htmlFor="label-phone-number-1">Number </label>
                                                            </div>
                                                            <div role="alert" className="form-input col-xs-12  col-no-gutter">
                                                                <Controller
                                                                    name={`primaryPhone`}
                                                                    control={control}
                                                                    defaultValue={personalAccountInformation.primaryPhone}
                                                                    className="inputbox"
                                                                    rules={{
                                                                        required: true,
                                                                        validate: (value) =>
                                                                            value
                                                                                ? isValidPhoneNo(
                                                                                      accountInfo && !!accountInfo.country?.replaceAll(/\s/g, "")
                                                                                          ? accountInfo.country
                                                                                          : "US",
                                                                                      value
                                                                                  )
                                                                                : false,
                                                                    }}
                                                                    aria-required="true"
                                                                    render={({ onChange, value }) => (
                                                                        <PhoneInput
                                                                            value={value}
                                                                            onChange={onChange}
                                                                            international={false}
                                                                            addInternationalOption={false}
                                                                            defaultCountry={
                                                                                accountInfo && !!accountInfo.country?.replaceAll(/\s/g, "")
                                                                                    ? accountInfo.country
                                                                                    : "US"
                                                                            }
                                                                            countries={[
                                                                                accountInfo && !!accountInfo.country?.replaceAll(/\s/g, "")
                                                                                    ? accountInfo.country
                                                                                    : "US",
                                                                            ]}
                                                                            id="label-phone-number"
                                                                            placeholder={tLang(
                                                                                "manageAccount.myAccount.businessAccountAndContactInformation.placeholder.primaryPhone"
                                                                            )}
                                                                            maxLength={15}
                                                                            minLength={10}
                                                                            aria-required="true"
                                                                        />
                                                                    )}
                                                                />
                                                                {!!errors["primaryPhone"] && errors["primaryPhone"].type === "required" && (
                                                                    <span className="form-error">
                                                                        {tLang(
                                                                            "manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryPhone.required"
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {!!errors["primaryPhone"] && errors["primaryPhone"].type === "validate" && (
                                                                    <span className="form-error">
                                                                        {tLang(
                                                                            "manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryPhone.invalid"
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {!!errors["primaryPhone"] &&
                                                                    errors["primaryPhone"].type === "minLength" &&
                                                                    (getCountryInfo() ? (
                                                                        <span className="form-error">
                                                                            {tLang(
                                                                                "manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryPhone.phoneNumberMinimum1"
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="form-error">
                                                                            {tLang(
                                                                                "manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryPhone.phoneNumberInvalid"
                                                                            )}
                                                                        </span>
                                                                    ))}
                                                                {!!errors["primaryPhone"] &&
                                                                    errors["primaryPhone"].type === "maxLength" &&
                                                                    (getCountryInfo() ? (
                                                                        <span className="form-error">
                                                                            {tLang(
                                                                                "manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryPhone.phoneNumberMinimum2"
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="form-error">
                                                                            {tLang(
                                                                                "manageAccount.myAccount.businessAccountAndContactInformation.validation.primaryPhone.phoneNumberInvalid"
                                                                            )}
                                                                        </span>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                                <div className={errors["fax"] ? "form-row row status-error" : "form-row row"}>
                                    <div className="form-row inline row">
                                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                            <label htmlFor="label-fax">{tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.fax")} </label>
                                        </div>
                                        <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                            {getCountryInfo() ? (
                                                <input
                                                    type="text"
                                                    className="inputbox"
                                                    id="label-fax"
                                                    name={`fax`}
                                                    placeholder={tLang("manageAccount.myAccount.businessAccountAndContactInformation.placeholder.fax")}
                                                    defaultValue={maskPhoneNumber(businessInformation.fax)}
                                                    ref={validationFax}
                                                />
                                            ) : (
                                                <CommonPhoneInputFields
                                                    NAME="fax"
                                                    DefaultVal={maskPhoneNumber(businessInformation.fax)}
                                                    PLACEHOLDER={tLang("manageAccount.myAccount.businessAccountAndContactInformation.placeholder.fax")}
                                                    REF={validationFax}
                                                    ID="label-fax"
                                                />
                                            )}

                                            {!!errors["fax"] &&
                                                errors["fax"].type === "pattern" &&
                                                (getCountryInfo() ? (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.fax.pattern")}
                                                    </span>
                                                ) : (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.fax.invalid")}
                                                    </span>
                                                ))}
                                            {!!errors["fax"] &&
                                                errors["fax"].type === "minLength" &&
                                                (getCountryInfo() ? (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.fax.faxNumberMinimum1")}
                                                    </span>
                                                ) : (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.fax.invalid")}
                                                    </span>
                                                ))}
                                            {!!errors["fax"] &&
                                                errors["fax"].type === "maxLength" &&
                                                (getCountryInfo() ? (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.fax.faxNumberMinimum2")}
                                                    </span>
                                                ) : (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.fax.invalid")}
                                                    </span>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {creditProvider != null && creditProvider === "CREDITSAFE" && (
                                    <div className={errors["ein"] ? "form-row row status-error" : "form-row row"}>
                                        <div className="form-row inline row">
                                            <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                                <label htmlFor="label-ein">
                                                    {tLang("manageAccount.myAccount.businessAccountAndContactInformation.label.ein")}{" "}
                                                </label>
                                            </div>
                                            <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                                <input
                                                    type="text"
                                                    className="inputbox"
                                                    id="label-ein"
                                                    placeholder={tLang("manageAccount.myAccount.businessAccountAndContactInformation.placeholder.ein")}
                                                    name="ein"
                                                    defaultValue={businessInformation && formatEINEditView(piiState?._businessInformation?.ein)}
                                                    ref={register({ pattern: PATTERNS.EIN.pattern })}
                                                    onChange={onEINChangeHandler}
                                                    onKeyDown={updatEINFieldPressedKey}
                                                />

                                                {errors.ein && errors.ein.type === "pattern" && (
                                                    <span className="form-error">
                                                        {tLang("manageAccount.myAccount.businessAccountAndContactInformation.validation.ein.invalid")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="margin-vertical-l"></div>

                                <div className="row between-xs">
                                    <div className="col-xs col-no-gutter flex-grow-null"></div>
                                    <div className="col-xs col-no-gutter flex-grow-null">
                                        <ul className="list-inline list-reset">
                                            <li>
                                                <button
                                                    type="button"
                                                    className="btn-default btn-link btn-no-padding btn-no-border btn-min-width"
                                                    onClick={() => hideEditForm()}
                                                >
                                                    <span className="btn-text">{tLang("manageAccount.myAccount.membershipInformation.cancelButtonText")}</span>
                                                </button>
                                            </li>
                                            <li className="margin-left-s">
                                                <button type="submit" className="btn-default btn-primary btn-min-width" disabled={isSubmitting}>
                                                    <span className="btn-text">{tLang("manageAccount.myAccount.membershipInformation.saveButtonText")}</span>
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default BusinessAccountAndContactInformation;
